import { useEffect, useRef, useState } from 'react'
import { Zap, User, Building2, Home, Upload, CheckCircle2, Sparkles, Loader2, X, AlertTriangle } from 'lucide-react'
import api from '../api/client'
import SearchableSelect from './SearchableSelect'
import { successModal, errorModal, confirmModal } from './ui'

// ───────── validadores + máscaras ─────────
const soDig = (s) => (s || '').replace(/\D/g, '')
function cpfValido(v) {
  const c = soDig(v); if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false
  let s = 0; for (let i = 0; i < 9; i++) s += +c[i] * (10 - i)
  let d = 11 - (s % 11); if (d >= 10) d = 0; if (d !== +c[9]) return false
  s = 0; for (let i = 0; i < 10; i++) s += +c[i] * (11 - i)
  d = 11 - (s % 11); if (d >= 10) d = 0; return d === +c[10]
}
function cnpjValido(v) {
  const c = soDig(v); if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false
  const calc = (base) => { let s = 0, p = base.length - 7; for (let i = 0; i < base.length; i++) { s += +base[i] * p--; if (p < 2) p = 9 } const r = s % 11; return r < 2 ? 0 : 11 - r }
  if (calc(c.slice(0, 12)) !== +c[12]) return false
  return calc(c.slice(0, 13)) === +c[13]
}
const emailValido = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '')
function mascara(mask, v) {
  const d = soDig(v)
  if (mask === 'cpf') return d.slice(0, 11).replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2')
  if (mask === 'cnpj') return d.slice(0, 14).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
  if (mask === 'tel') { const t = d.slice(0, 11); return t.length <= 10 ? t.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2') : t.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2') }
  if (mask === 'cep') return d.slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2')
  if (mask === 'number') return d
  return v
}

const CIVIL = ['Solteiro', 'Casado', 'União Estável', 'Divorciado', 'Viúvo']
const TIPO_COND = ['Residencial', 'Comercial', 'Misto']

const TIPOS = [
  { v: 'pf', label: 'Pessoa Física', icon: User },
  { v: 'pj', label: 'Pessoa Jurídica', icon: Building2 },
  { v: 'condominio', label: 'Condomínio', icon: Home },
]
const ENDERECO = [
  ['cep', 'CEP', 'cliente', false, 'cep'], ['endereco', 'Endereço', 'cliente'], ['numero', 'Número', 'custom'],
  ['complemento', 'Complemento', 'custom'], ['bairro', 'Bairro', 'custom'],
  ['cidade', 'Cidade', 'cliente'], ['estado', 'Estado (UF)', 'cliente'],
]
// campo: [key, label, alvo, req, mask, opts]
const GD = {
  pf: {
    classe: ['Residencial', 'Comercial'],
    blocos: [
      { titulo: 'Dados do Cliente', campos: [
        ['nome', 'Nome Completo', 'cliente', true], ['cpf_cnpj', 'CPF', 'cliente', true, 'cpf'],
        ['data_nascimento', 'Data de Nascimento', 'cliente', false, 'date'],
        ['profissao', 'Profissão', 'custom'], ['estado_civil', 'Estado Civil', 'custom', false, 'select', CIVIL],
        ['nacionalidade', 'Nacionalidade', 'custom'],
        ['rg', 'RG (opcional)', 'custom'], ['orgao_emissor', 'Órgão Emissor', 'custom'], ['uf_rg', 'UF do RG', 'custom'],
        ['celular', 'Celular', 'custom', false, 'tel'], ['whatsapp', 'WhatsApp', 'cliente', false, 'tel'], ['email', 'E-mail', 'cliente', true, 'email'],
      ] },
      { titulo: 'Endereço da Unidade Consumidora', campos: ENDERECO },
    ],
    docs: [['conta_luz', 'Conta de Luz Atualizada', true], ['doc_foto', 'Documento com Foto (RG/CNH)', true], ['procuracao', 'Procuração (quando aplicável)']],
  },
  pj: {
    classe: ['Comercial', 'Industrial'],
    blocos: [
      { titulo: 'Dados da Empresa', campos: [
        ['nome', 'Razão Social', 'cliente', true], ['nome_fantasia', 'Nome Fantasia', 'custom'],
        ['cpf_cnpj', 'CNPJ', 'cliente', true, 'cnpj'], ['inscricao_estadual', 'Inscrição Estadual', 'custom'], ['segmento', 'Segmento', 'custom'],
      ] },
      { titulo: 'Responsável Legal', campos: [
        ['resp_nome', 'Nome Completo', 'custom', true], ['resp_cargo', 'Cargo do Responsável', 'custom'],
        ['resp_cpf', 'CPF', 'custom', true, 'cpf'], ['resp_nacionalidade', 'Nacionalidade', 'custom'],
        ['resp_estado_civil', 'Estado Civil', 'custom', false, 'select', CIVIL], ['resp_profissao', 'Profissão', 'custom'],
        ['resp_telefone', 'Telefone', 'custom', false, 'tel'], ['whatsapp', 'WhatsApp', 'cliente', false, 'tel'], ['email', 'E-mail', 'cliente', true, 'email'],
      ] },
      { titulo: 'Endereço da Unidade Consumidora', campos: ENDERECO },
    ],
    docs: [['conta_luz', 'Conta de Luz Atualizada', true], ['cartao_cnpj', 'Cartão CNPJ', true], ['doc_responsavel', 'Documento do Responsável', true], ['contrato_social', 'Contrato Social', true], ['procuracao', 'Procuração (quando aplicável)']],
  },
  condominio: {
    classe: ['Condomínio'],
    blocos: [
      { titulo: 'Dados do Condomínio', campos: [
        ['nome', 'Nome do Condomínio', 'cliente', true], ['cpf_cnpj', 'CNPJ', 'cliente', true, 'cnpj'],
        ['tipo_condominio', 'Tipo do Condomínio', 'custom', false, 'select', TIPO_COND], ['qtd_unidades', 'Quantidade de Unidades', 'custom', false, 'number'],
        ['administradora', 'Nome da Administradora', 'custom'], ['resp_financeiro', 'Responsável Financeiro', 'custom'],
      ] },
      { titulo: 'Síndico / Responsável', campos: [
        ['resp_nome', 'Síndico (Nome)', 'custom', true], ['resp_cpf', 'CPF do Síndico', 'custom', true, 'cpf'],
        ['resp_telefone', 'Telefone', 'custom', false, 'tel'], ['email', 'E-mail', 'cliente', true, 'email'],
      ] },
      { titulo: 'Endereço da Unidade Consumidora', campos: ENDERECO },
    ],
    docs: [['conta_luz', 'Conta de Luz Atualizada', true], ['cartao_cnpj', 'Cartão CNPJ', true], ['ata_assembleia', 'Ata de eleição do síndico', true], ['convencao', 'Convenção do condomínio (quando exigida)'], ['procuracao', 'Procuração da administradora (quando houver)']],
  },
}
const ETAPAS = ['Dados do Cliente', 'Endereço', 'Unidade Consumidora', 'Documentos', 'Revisão', 'Envio']
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

export default function ConexaoGD({ ofertaSel, isAdmin, licId, meuLic, onCriado }) {
  const DRAFT_KEY = 'tc_conexao_gd_draft'
  const [tipo, setTipo] = useState('')
  const [f, setF] = useState({})
  const [energia, setEnergia] = useState({ distribuidora_id: '', numero_instalacao: '', tipo_ligacao: 'monofasico', classe_consumo: '', media_consumo: '', valor_conta: '', taxa_iluminacao: '' })
  const [docs, setDocs] = useState({})
  const [distribuidoras, setDist] = useState([])
  const [msg, setMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const [iaOpen, setIaOpen] = useState(false)
  const [dup, setDup] = useState([])
  const [regras, setRegras] = useState(null)   // regras herdadas do Gerador + simulador (PRD)
  const draftTimer = useRef(null)

  useEffect(() => { api.get('/distribuidoras?limit=500').then((r) => setDist(r.data.data || [])).catch(() => {}) }, [])

  // Importação automática das regras (Estado→Distribuidora→Gerador) + simulador inteligente.
  useEffect(() => {
    const did = energia.distribuidora_id
    if (!did) { setRegras(null); return }
    let alive = true
    const params = {}
    if (energia.media_consumo) params.consumo_kwh = energia.media_consumo
    if (energia.valor_conta) params.valor_conta = energia.valor_conta
    const t = setTimeout(() => {
      api.get(`/distribuidoras/${did}/regras`, { params })
        .then((r) => { if (alive) setRegras(r.data) })
        .catch(() => { if (alive) setRegras(null) })
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [energia.distribuidora_id, energia.media_consumo, energia.valor_conta])

  // ── Rascunho automático: retomar ao abrir ──
  useEffect(() => {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (raw) {
      confirmModal('Encontramos um rascunho salvo automaticamente. Deseja continuar de onde parou?').then((ok) => {
        if (ok) { try { const d = JSON.parse(raw); setTipo(d.tipo || ''); setF(d.f || {}); setEnergia((p) => ({ ...p, ...(d.energia || {}) })) } catch { /* */ } }
        else localStorage.removeItem(DRAFT_KEY)
      })
    }
  }, [])
  // ── Rascunho automático: salvar 5s após parar de digitar + ao sair ──
  useEffect(() => {
    if (!tipo) return
    if (draftTimer.current) clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ tipo, f, energia })) } catch { /* */ } }, 5000)
    return () => draftTimer.current && clearTimeout(draftTimer.current)
  }, [tipo, f, energia])
  useEffect(() => {
    const onLeave = () => { if (tipo) { try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ tipo, f, energia })) } catch { /* */ } } }
    window.addEventListener('beforeunload', onLeave)
    return () => window.removeEventListener('beforeunload', onLeave)
  }, [tipo, f, energia])

  const cfg = tipo ? GD[tipo] : null
  const setCampo = (k, mask) => (e) => setF({ ...f, [k]: mask ? mascara(mask, e.target.value) : e.target.value })
  const setE = (k) => (e) => setEnergia({ ...energia, [k]: e.target.value })

  // Detecção de duplicados (CPF/CNPJ ou UC já cadastrados) — aviso não-bloqueante
  async function checkDup() {
    const cpf = f.cpf_cnpj || ''
    const uc = energia.numero_instalacao || ''
    if (!cpf && !uc) { setDup([]); return }
    try {
      const { data } = await api.get('/contratos-adesao/_check-duplicado', { params: { cpf_cnpj: cpf || undefined, numero_instalacao: uc || undefined } })
      setDup(data?.itens || [])
    } catch { /* silencioso */ }
  }
  const setDoc = (k) => (e) => setDocs({ ...docs, [k]: e.target.files?.[0] || null })

  async function cepBlur(cep) {
    const c = soDig(cep); if (c.length !== 8) return
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`); const d = await r.json()
      if (!d.erro) setF((p) => ({ ...p, endereco: d.logradouro || p.endereco, bairro: d.bairro || p.bairro, cidade: d.localidade || p.cidade, estado: d.uf || p.estado }))
    } catch { /* offline: segue manual */ }
  }

  // distribuidoras do estado selecionado (inteligência Estado × Distribuidora)
  const uf = (f.estado || '').toUpperCase()
  const distFiltradas = (uf && distribuidoras.some((d) => d.estado === uf)) ? distribuidoras.filter((d) => d.estado === uf) : distribuidoras

  // ── Barra de progresso ──
  const step = {
    dados: !!(f.nome && f.cpf_cnpj && f.email),
    endereco: !!(f.cep && f.cidade && f.estado),
    uc: !!(energia.distribuidora_id && energia.numero_instalacao),
    docs: cfg ? cfg.docs.filter(([, , req]) => req).every(([k]) => docs[k]) : false,
  }
  const feitos = [step.dados, step.endereco, step.uc, step.docs].filter(Boolean).length
  const pct = cfg ? Math.round((feitos / 4) * 100) : 0

  // ── Checklist de documentos (%) ──
  const docsReq = cfg ? cfg.docs.filter(([, , req]) => req) : []
  const docsEnviados = docsReq.filter(([k]) => docs[k]).length
  const docsPct = docsReq.length ? Math.round((docsEnviados / docsReq.length) * 100) : 0

  function aplicarIA(dados) {
    if (!dados) return
    const dig = soDig(dados.cpf_cnpj)
    const t = dig.length === 14 ? 'pj' : 'pf'
    setTipo(t)
    setF((p) => ({
      ...p, nome: dados.nome || p.nome, cpf_cnpj: dados.cpf_cnpj || p.cpf_cnpj,
      cep: dados.cep || p.cep, endereco: dados.endereco || p.endereco, bairro: dados.bairro || p.bairro,
      cidade: dados.cidade || p.cidade, estado: (dados.estado || p.estado || '').toUpperCase(),
    }))
    const dsel = distribuidoras.find((d) => (d.nome || '').toLowerCase().includes((dados.distribuidora || '').toLowerCase().slice(0, 5)) && dados.distribuidora)
    setEnergia((p) => ({
      ...p, numero_instalacao: dados.numero_instalacao || p.numero_instalacao,
      media_consumo: dados.media_consumo || p.media_consumo,
      classe_consumo: (dados.classe ? dados.classe[0].toUpperCase() + dados.classe.slice(1) : p.classe_consumo),
      distribuidora_id: dsel ? dsel.id : p.distribuidora_id,
      valor_conta: (dados.valor_conta ?? p.valor_conta),
      taxa_iluminacao: (dados.taxa_iluminacao ?? p.taxa_iluminacao),
      tipo_ligacao: (dados.tipo_ligacao || p.tipo_ligacao),
    }))
  }

  async function salvar() {
    setMsg('')
    if (!tipo) return setMsg('Selecione o tipo de cliente.')
    const licFinal = isAdmin ? Number(licId) : meuLic?.id
    if (!licFinal) return setMsg('Licenciado responsável não identificado.')
    for (const b of cfg.blocos) for (const [k, l, , req, mask] of b.campos) {
      if (req && !f[k]) return setMsg(`Preencha: ${l}`)
      if (mask === 'cpf' && f[k] && !cpfValido(f[k])) return setMsg(`CPF inválido em: ${l}`)
      if (mask === 'cnpj' && f[k] && !cnpjValido(f[k])) return setMsg(`CNPJ inválido em: ${l}`)
      if (mask === 'email' && f[k] && !emailValido(f[k])) return setMsg(`E-mail inválido em: ${l}`)
    }
    if (!energia.distribuidora_id) return setMsg('Selecione a distribuidora.')
    if (!energia.numero_instalacao) return setMsg('Informe o número da instalação (UC).')
    for (const [k, l, req] of cfg.docs) if (req && !docs[k]) return setMsg(`Envie o documento: ${l}`)

    setSaving(true)
    try {
      const cli = { licenciado_id: isAdmin ? licFinal : undefined, oferta_id: Number(ofertaSel.id) }
      const custom = { tipo_titular: tipo }
      // PRD 'Ajuste final': persistir valor da conta + Taxa de Iluminação Pública (COSIP/CIP)
      if (energia.valor_conta) custom.valor_fatura = Number(energia.valor_conta)
      if (energia.taxa_iluminacao) custom.taxa_iluminacao = Number(energia.taxa_iluminacao)
      for (const b of cfg.blocos) for (const [k, , alvo] of b.campos) {
        const val = f[k]; if (val === undefined || val === '') continue
        if (alvo === 'cliente') cli[k] = val; else custom[k] = val
      }
      const { data: c } = await api.post('/clientes', cli)
      const body = {
        licenciado_id: licFinal, cliente_id: c.id, tipo_conexao: 'energia', tipo_titular: tipo,
        oferta_id: Number(ofertaSel.id), pv: ofertaSel.pv || 0,
        distribuidora_id: Number(energia.distribuidora_id), numero_instalacao: energia.numero_instalacao,
        tipo_ligacao: energia.tipo_ligacao, tipo_instalacao: (energia.classe_consumo || '').toLowerCase() || null,
        media_consumo: energia.media_consumo ? Number(energia.media_consumo) : null,
        dados_customizados: custom,
      }
      const { data } = await api.post('/contratos-adesao', body)
      for (const [k, file] of Object.entries(docs)) {
        if (!file) continue
        const fd = new FormData(); fd.append('tipo', k); fd.append('file', file)
        try { await api.post(`/contratos-adesao/${data.id}/documentos`, fd) } catch { /* segue */ }
      }
      localStorage.removeItem(DRAFT_KEY)
      successModal('Cadastro enviado para a Fila de Análise. Você será notificado quando o administrador concluir a análise.', { title: 'Enviado para análise!' })
      onCriado(data)
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao criar conexão GD') } finally { setSaving(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Zap size={16} className="text-accent-500" /> Energia GD: <b className="text-gray-900">{ofertaSel?.nome}</b> · {ofertaSel?.pv} PV
        </div>
        <button onClick={() => setIaOpen(true)} className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-3 py-2 text-sm font-semibold shadow">
          <Sparkles size={15} /> Nova Conexão com IA
        </button>
      </div>

      {/* Barra de progresso */}
      {cfg && (
        <div>
          <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
            <span>{ETAPAS.slice(0, 4).map((e, i) => <span key={e} className={i < feitos ? 'text-accent-600 font-semibold' : ''}>{e}{i < 3 ? ' · ' : ''}</span>)}</span>
            <span className="font-semibold text-gray-600">{pct}%</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-primary-400 to-accent-500 transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {/* Tipo de cliente */}
      <div>
        <label className="block text-sm font-medium mb-2">Tipo de Cliente *</label>
        <div className="grid grid-cols-3 gap-2">
          {TIPOS.map(({ v, label, icon: Icon }) => (
            <button key={v} onClick={() => { setTipo(v); setEnergia((p) => ({ ...p, classe_consumo: GD[v].classe[0] })); if (v === 'pf' && !f.nacionalidade) setF((p) => ({ ...p, nacionalidade: 'Brasileiro' })) }}
              className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-sm ${tipo === v ? 'border-accent-400 ring-1 ring-accent-300 bg-accent-50/40' : 'border-gray-200'}`}>
              <Icon size={20} className={tipo === v ? 'text-accent-600' : 'text-gray-400'} /> {label}
            </button>
          ))}
        </div>
      </div>

      {cfg && (
        <>
          {cfg.blocos.map((b) => (
            <div key={b.titulo} className="border-t border-gray-100 pt-3">
              <p className="text-sm font-semibold text-gray-800 mb-2">{b.titulo}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {b.campos.map(([k, l, , req, mask, opts]) => {
                  const val = f[k] || ''
                  const erro = val && ((mask === 'cpf' && !cpfValido(val)) || (mask === 'cnpj' && !cnpjValido(val)) || (mask === 'email' && !emailValido(val)))
                  return (
                    <div key={k}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{l}{req ? ' *' : ''}</label>
                      {mask === 'select' ? (
                        <select className={inp} value={val} onChange={setCampo(k)}>
                          <option value="">Selecione...</option>
                          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className={`${inp} ${erro ? 'border-red-300' : ''}`} type={mask === 'date' ? 'date' : 'text'} value={val}
                          onChange={setCampo(k, ['cpf', 'cnpj', 'tel', 'cep', 'number'].includes(mask) ? mask : null)}
                          onBlur={k === 'cep' ? (e) => cepBlur(e.target.value) : (k === 'cpf_cnpj' ? checkDup : undefined)} />
                      )}
                      {erro && <p className="text-[10px] text-red-500 mt-0.5">{mask === 'email' ? 'E-mail inválido' : (mask || '').toUpperCase() + ' inválido'}</p>}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Energia */}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-sm font-semibold text-gray-800 mb-2">Unidade Consumidora / Energia</p>
            {uf && <p className="text-[11px] text-gray-400 mb-2">Distribuidoras disponíveis em {uf}.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Distribuidora *</label>
                <SearchableSelect value={energia.distribuidora_id} onChange={(v) => setEnergia({ ...energia, distribuidora_id: v })}
                  placeholder="Buscar distribuidora..." options={distFiltradas.map((d) => ({ value: d.id, label: `${d.nome} (${d.estado})` }))} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Número da Instalação (UC) *</label><input className={inp} value={energia.numero_instalacao} onChange={setE('numero_instalacao')} onBlur={checkDup} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Ligação</label>
                <select className={inp} value={energia.tipo_ligacao} onChange={setE('tipo_ligacao')}>
                  <option value="monofasico">Monofásica</option><option value="bifasico">Bifásica</option><option value="trifasico">Trifásica</option>
                </select></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Classe de Consumo</label>
                <select className={inp} value={energia.classe_consumo} onChange={setE('classe_consumo')}>
                  {cfg.classe.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Média de Consumo (kWh)</label><input className={inp} type="number" value={energia.media_consumo} onChange={setE('media_consumo')} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Valor médio da conta (R$)</label><input className={inp} type="number" value={energia.valor_conta || ''} onChange={setE('valor_conta')} placeholder="p/ simular economia" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Taxa de Iluminação Pública — COSIP/CIP (R$)</label><input className={inp} type="number" value={energia.taxa_iluminacao || ''} onChange={setE('taxa_iluminacao')} placeholder="preenchida pela IA · editável" /></div>
            </div>

            {/* Regras importadas automaticamente + Simulador Inteligente (PRD) */}
            {regras?.regras?.gerador && (
              <div className="mt-3 rounded-xl border border-accent-200 bg-accent-50/40 p-3">
                <p className="text-xs font-bold text-accent-800 mb-2">Condições importadas automaticamente</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-4 text-[12px] text-neutral-700">
                  <span>Tipo de fatura: <b>{regras.regras.tipo_fatura || '—'}</b></span>
                  <span>Desconto ao cliente: <b>{regras.regras.desconto_aplicado ?? 0}%</b></span>
                  <span>Consumo mínimo: <b>{regras.regras.consumo_minimo_kwh ?? '—'} kWh</b></span>
                  <span>Prazo de injeção: <b>{regras.regras.prazo_medio_injecao_dias ?? '—'} dias</b></span>
                  <span>Troca de titularidade: <b>{regras.regras.troca_titularidade ? 'Sim' : 'Não'}</b></span>
                  <span>Análise de crédito: <b>{regras.regras.requer_analise_credito ? 'Sim' : 'Não'}</b></span>
                </div>
                {regras.simulacao && Number(regras.simulacao.valor_conta) > 0 && (
                  <div className="mt-2 pt-2 border-t border-accent-200 grid grid-cols-2 sm:grid-cols-4 gap-y-1 gap-x-4 text-[12px]">
                    <span className="text-neutral-600">Novo valor: <b>{'R$ ' + Number(regras.simulacao.novo_valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</b></span>
                    <span className="text-emerald-700">Economia mensal: <b>{'R$ ' + Number(regras.simulacao.economia_mensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</b></span>
                    <span className="text-emerald-700">Economia anual: <b>{'R$ ' + Number(regras.simulacao.economia_anual).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</b></span>
                    <span className="text-neutral-600">Desconto: <b>{regras.simulacao.percentual_desconto}%</b></span>
                  </div>
                )}
              </div>
            )}
            {regras?.regras?.avisos && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-800 flex items-start gap-2">
                <span className="font-bold">Aviso:</span> {regras.regras.avisos}
              </div>
            )}
          </div>

          {/* Documentos + checklist % */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-800">Documentos</p>
              <span className="text-[11px] text-gray-500">{docsPct}% dos obrigatórios enviados</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2"><div className="h-full bg-emerald-400" style={{ width: `${docsPct}%` }} /></div>
            <div className="space-y-2">
              {cfg.docs.map(([k, l, req]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  {docs[k] ? <CheckCircle2 size={15} className="text-emerald-500" /> : <Upload size={15} className="text-gray-400" />}
                  <span className="flex-1">{l}{req ? ' *' : ''}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${docs[k] ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{docs[k] ? 'Enviado' : 'Pendente'}</span>
                  <span className="text-[11px] text-accent-600 max-w-[120px] truncate">{docs[k] ? docs[k].name : 'Enviar'}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={setDoc(k)} />
                </label>
              ))}
            </div>
          </div>

          {dup.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={15} /> Possível cadastro duplicado</p>
              <p className="text-[11px] text-amber-700 mt-0.5">Já existe conexão para este CPF/CNPJ ou UC:</p>
              <ul className="mt-1 space-y-0.5">
                {dup.slice(0, 3).map((d) => (
                  <li key={d.id} className="text-[11px] text-amber-800">• #{d.id} {d.cliente_nome || ''} {d.numero_instalacao ? `· UC ${d.numero_instalacao}` : ''} <span className="text-amber-600">({d.status_label})</span></li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-600 mt-1">Você ainda pode cadastrar, mas verifique se não é repetido.</p>
            </div>
          )}
          <button onClick={salvar} disabled={saving} className="rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow disabled:opacity-50">
            {saving ? 'Registrando...' : 'Cadastrar conexão GD'}
          </button>
          {msg && <p className="text-sm text-tc-graphite bg-amber-50 rounded px-3 py-2">{msg}</p>}
        </>
      )}

      {iaOpen && <ModalIA onClose={() => setIaOpen(false)} onDados={(d) => { aplicarIA(d); setIaOpen(false) }} />}
    </div>
  )
}

function ModalIA({ onClose, onDados }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const escolher = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    setErr(''); setBusy(true)
    const rd = new FileReader()
    rd.onload = () => {
      api.post('/conexao/ia-extrair', { arquivo_base64: rd.result })
        .then((r) => {
          if (r.data?.legivel === false) {
            setErr('Documento ilegível — não consegui identificar os dados. Tente uma foto mais nítida (boa luz, sem cortes) ou preencha manualmente.')
            return
          }
          if (r.data?.dados) { onDados(r.data.dados); successModal('Conta lida! Confira e complete os dados antes de salvar.', { title: 'Leitura por IA' }) }
          else setErr('Não foi possível ler.')
        })
        .catch((e2) => setErr(e2?.response?.data?.detail || 'Falha ao ler a conta.'))
        .finally(() => setBusy(false))
    }
    rd.readAsDataURL(file)
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-bold flex items-center gap-2"><Sparkles size={18} className="text-violet-600" /> Nova Conexão com IA</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-500">Envie uma <b>foto ou print da conta de energia</b>. A IA lê e preenche o formulário automaticamente — você confere antes de salvar.</p>
        {err && <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{err}</p>}
        {busy ? (
          <div className="flex flex-col items-center justify-center gap-2 text-gray-500 py-8"><Loader2 size={20} className="animate-spin text-violet-600" /> <span>IA analisando a conta...</span><span className="text-[11px] text-gray-400">Isso leva alguns segundos</span></div>
        ) : (
          <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-8 cursor-pointer hover:border-accent-300">
            <Upload size={26} className="text-gray-400" />
            <span className="text-sm text-gray-500">Toque para enviar a foto/print ou PDF da conta</span>
            <span className="text-[11px] text-gray-400">JPG, PNG ou PDF</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={escolher} />
          </label>
        )}
      </div>
    </div>
  )
}
